// VideoListScreen.jsx
import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { AppContext } from "./AppContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore, database } from "./firebase";
import { ref, get } from "firebase/database";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentFlowDynamic from "./PaymentFlowDynamic";
import Modal from "react-modal";
import { MdContentCopy } from "react-icons/md";
import { doc, updateDoc, collection, getDocs } from "firebase/firestore";

const stripePromise = loadStripe(
  "pk_test_51QX2ZHBqCybQOhresLkkgROQmPTR1RAB3R4Z3TQsWQrRCSLFs6RmolWk3XYTxvhCVs8apE5UB3q9C8UMaOZfukX200xBarjuS8"
);

const width = Math.max(window.innerWidth, 320);
const height = Math.max(window.innerHeight, 640);
const bottomAreaHeight = height * 0;

/* ---------- number→words helpers (kept for royalty text) ---------- */
const ones = ["zero","one","two","three","four","five","six","seven","eight","nine"];
const teens = ["ten","eleven","twelve","thirteen","fourteen","fifteen","sixteen","seventeen","eighteen","nineteen"];
const tens  = ["","","twenty","thirty","forty","fifty","sixty","seventy","eighty","ninety"];
function numToWords(n){
  if(n<10) return ones[n];
  if(n<20) return teens[n-10];
  if(n<100){
    const t=Math.floor(n/10),o=n%10;
    return tens[t]+(o?` ${ones[o]}`:"");
  }
  return n.toString();
}
function percentageToWords(p){
  const str=p.toFixed(2);
  const [i,d="00"]=str.split(".");
  const intWords=numToWords(parseInt(i,10));
  const decWords=d.split("").map(v=>numToWords(parseInt(v,10))).join(" ");
  return `${intWords} point ${decWords} percent (${str}%)`;
}

/* ---------- misc helpers ---------- */
const STRIPE_PERCENT_FEE = 0.029;
const STRIPE_FIXED_FEE   = 0.30;
function calcStripeFee(amountUSD){ return +(amountUSD*STRIPE_PERCENT_FEE+STRIPE_FIXED_FEE).toFixed(2); }

const toNum = (n) => {
  if (n == null) return 0;
  if (typeof n === "number") return Number.isFinite(n) ? n : 0;
  if (typeof n === "string") return Number(n) || 0;
  if (typeof n === "object") return toNum(Object.values(n)[0]);
  return 0;
};
const fmtUSD = (n) => Number(n || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});

const fmt3 = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });


/* ───────────────── Asset row ───────────────── */
function AssetItem({ asset, onOpenDatChart, navigate }) {
  const { userId2, userWallet } = useContext(AppContext);
  const [earnings, setEarnings] = useState({ streams:0, sales:0, totalStreams:0 });

  useEffect(()=>{
    const streamsRef = ref(database, `earnings/${asset.id}/totalEarnings`);
    const salesRef   = ref(database, `earnings/${asset.id}/sales/totalEarnings`);
    const totalStrm  = ref(database, `streamStats/${asset.id}/totalEarnings`);
    (async()=>{
      try{
        const [stSnap, saSnap, tsSnap] = await Promise.all([get(streamsRef), get(salesRef), get(totalStrm)]);
        setEarnings({
          streams     : stSnap.exists() ? stSnap.val() : 0,
          sales       : saSnap.exists() ? saSnap.val() : 0,
          totalStreams: tsSnap.exists() ? tsSnap.val() : 0,
        });
      }catch(e){ console.error("earnings fetch err",e); }
    })();
  },[asset.id]);

  // RN context uses streams * 0.05 + sales; we don't render it directly here
  const unsoldPct = (asset.ownedEditions||[])
    .filter(ed=>{
      const w = userWallet?.ownedDats?.find(e=>e.edition===ed.edition);
      return w && (w.sold ?? false) === false;
    })
    .reduce((sum,ed)=>sum+(ed.royalties||0),0);

  // Align with RN: if creator, show 50 (not +50)
  const displayPct = (userId2===asset.creatorId) ? 50 : unsoldPct;

  const walletEarnings = (userWallet?.ownedDats||[])
    .filter(e=>e.id===asset.id)
    .reduce((sum,e)=>sum+(typeof e.earnings==="number"?e.earnings:0),0);

  return (
    <div
      style={styles.datItemContainer}
      onClick={() => navigate(`/wallet/dat/${asset.id}`, { state: { dat: asset } })}
    >
      <div style={styles.thumbWrapper}>
        <img src={asset.ArtworkURL} alt="" style={styles.datThumbnail}/>
      </div>

      <div style={styles.middleContainer}>
        <div style={styles.datName}>'{asset.SongName}'</div>
        <div style={styles.datRoyalty}>Royalty Share: {displayPct.toFixed(4)}%</div>
        <div style={styles.datEarnings}>Total Streams: {earnings.totalStreams.toLocaleString()}</div>
        <div style={styles.datEarnings}>Earnings: ${walletEarnings.toFixed(3)}</div>
      </div>

      <button
        style={styles.chartButton}
        onClick={(e)=>{ e.stopPropagation(); onOpenDatChart(asset); }}
      >
        View&nbsp;Chart
      </button>
    </div>
  );
}

export default function VideoListScreen() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { purchaseDetails } = location.state || {};

  const [confirmVisible,setConfirmVisible] = useState(false);
  const [purchaseData,setPurchaseData]     = useState(null);
  const [needsDatTokens, setNeedsDatTokens] = useState(false);

  const [stripeCS,       setStripeCS]       = useState(null);
  const [amountDueUSD,   setAmountDueUSD]   = useState("");
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [isPurchasing,   setIsPurchasing]   = useState(false);
  const [loadingPurchase,setLoadingPurchase]= useState(false);

  const [stripeFeeUSD, setStripeFeeUSD]     = useState(null);
  const [grandTotalUSD,setGrandTotalUSD]    = useState(null);

  const [walletSettingsVisible,setWalletSettingsVisible] = useState(false);
  const [walletIdView,setWalletIdView]                   = useState(false);
  const [checkingStripeAccount,setCheckingStripeAccount] = useState(false);
  const [creatingBillingPortal,setCreatingBillingPortal] = useState(false);
  const [switchingName,setSwitchingName]                 = useState(false);

  const [poolCents, setPoolCents] = useState(0);
const [circulating, setCirculating] = useState(0);
const [datTokenSpotUsd, setDatTokenSpotUsd] = useState(0);
const [datRewardWalletQty, setDatRewardWalletQty] = useState(0);


  const {
    // original:
    userAssets, userWallet, creditsRaw, setCurrentlyBuying, userId2, setAssetsSummary, currentUser, setMyPosts,
    // may exist (optional) for DATCHAIN totals:
    datWallet, creditsPurchased, creditsEarned,
    tokensUtility
  } = useContext(AppContext);

  /* handle incoming purchase details */
  useEffect(()=>{
    if (purchaseDetails){
      setPurchaseData(purchaseDetails);
      setConfirmVisible(true);

      // keep original behavior → clear the state so it doesn't re-open
      navigate(location.pathname, { replace:true });
    }
  },[purchaseDetails, navigate, location.pathname]);

  useEffect(() => {
  (async () => {
    try {
      const poolSnap = await get(ref(database, 'metrics/datToken/poolCents'));
      const circSnap = await get(ref(database, 'metrics/datToken/circulating'));
      const pool = Number(poolSnap.val() || 0);
      const circ = Number(circSnap.val() || 0);
      setPoolCents(pool);
      setCirculating(circ);
      setDatTokenSpotUsd(circ ? (pool / 100) / circ : 0); // cents → dollars
    } catch (e) {
      setPoolCents(0);
      setCirculating(0);
      setDatTokenSpotUsd(0);
      console.error('Token metric fetch error', e);
    }
  })();
}, []);

useEffect(() => {
  if (!userId2) return;
  (async () => {
    try {
      const snap = await get(ref(database, `datChainTokenWallets/${userId2}`));
      setDatRewardWalletQty(Number((snap.val() || {}).qty) || 0);
    } catch (e) {
      setDatRewardWalletQty(0);
      console.error('Reward wallet fetch error', e);
    }
  })();
}, [userId2]);


  const mapWalletBlock = (id, v) => {
    const price   = toNum(v?.price);
    const qty     = toNum(v?.qty);
    const unified = toNum(v?.rewards);
    const rewards = unified > 0 ? unified : (toNum(v?.rebate) + toNum(v?.internalRebate));
    return { id, price, qty, rewards };
  };
  const allBlocks = useMemo(()=>{
    if (!datWallet) return [];
    return Object.entries(datWallet).map(([id, v]) => mapWalletBlock(id, v));
  },[datWallet]);

  const totalsTop = useMemo(()=>{
    return allBlocks.reduce((acc,b)=>{
      const tokenUsd   = (b.qty||0)*b.price;
      const rewardsUsd = (b.rewards||0)*b.price;
      acc.tokens     += (b.qty||0)+(b.rewards||0);
      acc.tokenUsd   += tokenUsd;
      acc.rewardsUsd += rewardsUsd;
      acc.utilityUsd  = acc.tokenUsd + acc.rewardsUsd;
      return acc;
    },{tokens:0, tokenUsd:0, rewardsUsd:0, utilityUsd:0});
  },[allBlocks]);

  const totalCredits = useMemo(()=>{
    const p = toNum(creditsPurchased);
    const e = toNum(creditsEarned);
    if (p || e) return p+e;
    return toNum(creditsRaw);
  },[creditsPurchased, creditsEarned, creditsRaw]);

  const balanceTopUSD = useMemo(()=>{
    const creditsUsd = Number((totalCredits * 0.05).toFixed(2));
    const tokenUsd   = Number((totalsTop.tokenUsd   || 0).toFixed(2));
    const rewardsUsd = Number((totalsTop.rewardsUsd || 0).toFixed(2));
    return +(creditsUsd + tokenUsd + rewardsUsd).toFixed(2);
  },[totalCredits, totalsTop.tokenUsd, totalsTop.rewardsUsd]);

  const earnedCreditsUsd = useMemo(() => {
  return (Number(creditsEarned) || 0) * 0.10;
}, [creditsEarned]);

const myDatTokenValueUsd = useMemo(() => {
  return (Number(datRewardWalletQty) || 0) * (Number(datTokenSpotUsd) || 0);
}, [datRewardWalletQty, datTokenSpotUsd]);

const earningsTotalUsd = useMemo(() => {
  return (Number(earnedCreditsUsd) || 0) + (Number(myDatTokenValueUsd) || 0);
}, [earnedCreditsUsd, myDatTokenValueUsd]);


  /* token sufficiency check for modal text */
  useEffect(()=>{
    if (!purchaseData) return;
    const editionPriceUSD = toNum(purchaseData?.editionDetails?.price);
    const datChainBalanceUSD = (allBlocks||[]).reduce((sum,b)=>sum + (toNum(b.qty)+toNum(b.rewards))*toNum(b.price),0);
    setNeedsDatTokens(datChainBalanceUSD < editionPriceUSD);
  },[purchaseData, allBlocks]);

  /* discount / royalty text */
  const discountAndRoyaltyText = React.useMemo(()=>{
    if(!purchaseData || !userWallet) return { discount:"", royaltyDesc:"" };

    const assetEditions = purchaseData.nft.ownedEditions || purchaseData.nft.editions;
    let ownedPct = 0;
    assetEditions?.forEach(ed=>{
      const w = userWallet.ownedDats?.find(e=>e.id===purchaseData.nft.id && e.edition===ed.edition && !e.sold);
      if(w && ed.edition!==purchaseData.selectedEdition) ownedPct += ed.royalties||0;
    });

    let discount = "";
    if(ownedPct>0 && purchaseData.nft?.SoldOut){
      const save = purchaseData.editionDetails.price * (ownedPct/100);
      discount = `\nBased on your current ownership (${ownedPct.toFixed(2)}%), you'll save approximately $${save.toFixed(2)}.`;
    }

    const curEdPct  = purchaseData.editionDetails.royalties||0;
    const newTotal  = ownedPct + curEdPct;
    let royaltyDesc = `\nBy selecting Confirm, you are purchasing ${percentageToWords(curEdPct)} of the above mentioned DAT. You agree that you have reviewed all of the SPNDAT terms and services as it relates to the purchasing of DATS.\n\nA DAT is a Master recording as defined in our Terms Of Service.`;
    if(ownedPct>0){
      royaltyDesc += ` \n\nYour total ownership of this DAT will become ${percentageToWords(newTotal)}.`;
    }

    return { discount, royaltyDesc };
  },[purchaseData,userWallet]);

  async function toggleUseWalletIdFirebase({ userId2, newVal }) {
    await updateDoc(doc(firestore,"users",userId2),{ useWalletId:newVal });
    const summaries = await getDocs(collection(firestore,"socialSummaries"));
    const tasks = summaries.docs.map(async d=>{
      const data=d.data();
      if(!Array.isArray(data.posts)) return;
      const updated = data.posts.map(p=>{
        const copy={...p};
        if(copy.userId===userId2) copy.useWalletId=newVal;
        if(copy.originalPost?.userId===userId2){
          copy.originalPost={...copy.originalPost,useWalletId:newVal};
        }
        return copy;
      });
      await updateDoc(doc(firestore,"socialSummaries",d.id),{ posts:updated });
    });
    await Promise.all(tasks);
  }
  const copyWalletId = ()=>{
    if(currentUser?.walletId){
      navigator.clipboard.writeText(currentUser.walletId);
      alert("Wallet ID copied to clipboard");
    }
  };
  const openCashoutSettings = async ()=>{
    setCheckingStripeAccount(true);
    try{
      const fn = httpsCallable(getFunctions(),"createOrRetrieveAccount");
      const res = await fn({ email:currentUser.email });
      if(!res.data?.stripeAccountId || !res.data?.payouts_enabled){
        setWalletSettingsVisible(false);
        navigate("/cashout");
        return;
      }
      const linkFn = httpsCallable(getFunctions(),"createStripeOnboardingLinkIOS");
      const link   = await linkFn();
      window.open(link.data.url,"_blank");
      setWalletSettingsVisible(false);
    }catch(e){
      console.error(e);
      alert("Unable to open Stripe – try again.");
    }finally{ setCheckingStripeAccount(false); }
  };
  const openBillingSettings = ()=>{
    window.open("https://billing.stripe.com/p/login/14A8wO77gc5jgF58lHbsc00","_blank");
    setWalletSettingsVisible(false);
  };
  const toggleUseWalletId = async ()=>{
    setSwitchingName(true);
    const newVal = !currentUser?.useWalletId;
    try{
      await toggleUseWalletIdFirebase({ userId2, newVal });
      setMyPosts(prev=>prev.map(p=>{
        const copy={...p};
        if(copy.userId===userId2) copy.useWalletId=newVal;
        if(copy.originalPost?.userId===userId2){
          copy.originalPost={...copy.originalPost,useWalletId:newVal};
        }
        return copy;
      }));
      alert(`Display set to ${newVal?"Wallet ID":"Username"}.`);
    }catch(e){
      console.error(e);
      alert("Failed to update setting – try again.");
    }finally{
      setSwitchingName(false);
    }
  };

  const handleCancel = ()=>{
    setConfirmVisible(false);
    setPurchaseData(null);
    navigate(-1);
  };

  const handleConfirm = async () => {
    try {
      setConfirmVisible(false);
      setCurrentlyBuying?.(true);

      if (needsDatTokens) {
        setCurrentlyBuying?.(false);
        alert("You need more DATCHAIN Tokens to complete this purchase.");
        return;
      }

      const functions = getFunctions();
      const transactionProcessor = httpsCallable(functions, "transactionProcessor3DatToken");

      const buyer = userId2;
      const seller = purchaseData.editionDetails.currentOwnerId;
      const datId = purchaseData.nft.id;
      const edition = purchaseData.selectedEdition;
      const timestamp = Date.now();

      const response = await transactionProcessor({ buyer, seller, datId, edition, timestamp });
      const { success, error } = response.data || {};
      if (!success) throw new Error(error || "Purchase failed.");

      setCurrentlyBuying?.(false);
      setAssetsSummary?.(prev => (prev||[]).map(a =>
        a.id === datId ? { ...a, editions:(a.editions||[]).map(ed => ed.edition===edition ? { ...ed, isForSale:false } : ed ) } : a
      ));
      setPurchaseData(null);
      alert("Transaction successful!");
    } catch (err) {
      setCurrentlyBuying?.(false);
      setConfirmVisible(false);
      setPurchaseData(null);
      console.error(err);
      alert(`Transaction failed: ${err.message}`);
    }
  };

  const totalBalanceUSD = (creditsRaw*0.10).toFixed(2);

  return (
    <div style={styles.fullScreen}>
      <img src={require("./assets/BlankBG.png")} alt="" style={styles.fullScreenImage}/>

      {/* scrollable wallet list (unchanged positioning/scrolling) */}
      <div style={styles.walletContainer}>

        <div style={styles.scrollContainer}>
          {/* NEW: Header to mirror RN */}
          <div
            style={{
              color: "#fff",
              fontSize: 28,
              fontWeight: 700,
              padding: "0 6px",
              margin: "0 0 6px 4px"
            }}
          >
            Wallet
          </div>

<div style={styles.totalsGrid}>
  <div style={styles.metricCard}>
    <div style={styles.metricLabel}>{tokensUtility} UTILITY TOKENS</div>
    <div style={styles.metricValue}>${fmtUSD(totalsTop.utilityUsd)}</div>
  </div>

  <div style={styles.metricCard}>
    <div style={styles.metricLabel}>DATCHAIN TOKEN REWARDS</div>
    <div style={styles.metricValue}>
      {Number.parseInt(String(datRewardWalletQty ?? 0), 10).toLocaleString()}
    </div>
  </div>

  <div style={styles.metricCard}>
    <div style={styles.metricLabel}>EARNINGS</div>
    <div style={styles.metricValue}>${fmt3(earningsTotalUsd)}</div>
  </div>

  <div style={styles.metricCard}>
    <div style={styles.metricLabel}>VALUE PER DATCHAIN TOKEN</div>
    <div style={styles.metricValue}>${fmt3(datTokenSpotUsd)}</div>
  </div>
</div>



          {/* NEW: Buttons sit directly under totals (inside scroll area) */}
          <div style={styles.buttonRow}>
            <button style={styles.cashoutButton} onClick={()=>navigate("/cashout")}>Cash Out</button>
            <button style={styles.cashoutButton} onClick={()=>setWalletSettingsVisible(true)}>Settings</button>
          </div>

          {/* asset list */}
          {userAssets?.map(a=>(
            <AssetItem
              key={a.id}
              asset={a}
              navigate={navigate}
              onOpenDatChart={dat => navigate(`/wallet/analytics/${dat.id}`, { state: { dat } })}
            />
          ))}

          <div style={{ height: '200px' }}/>
        </div>
      </div>



      {/* Wallet Settings modal (unchanged) */}
      {walletSettingsVisible && (
        <div style={styles.modalOverlay}>
          <div style={styles.walletModalContent}>
            {(checkingStripeAccount || creatingBillingPortal) ? (
              <>
                <div className="spinner" style={{ marginBottom:20 }}/>
                <div style={styles.walletModalTitle}>
                  {checkingStripeAccount ? "Checking Stripe account…" : "Connecting to Stripe…"}
                </div>
              </>
            ) : (
              !walletIdView ? (
                <>
                  <div style={styles.walletModalTitle}>Wallet Settings</div>
                  <button style={styles.walletOptionButton} onClick={openCashoutSettings}>Edit Cash-out Details</button>
                  <button style={styles.walletOptionButton} onClick={openBillingSettings}>Edit Billing Info</button>
                  <button style={styles.walletOptionButton} onClick={()=>setWalletIdView(true)}>Wallet ID</button>
                  <button style={styles.walletOptionButton} onClick={()=>setWalletSettingsVisible(false)}>Close</button>
                </>
              ) : (
                <>
                  <div style={styles.walletModalTitle}>Your Wallet ID</div>
                  <div onClick={copyWalletId} style={{ display:"flex",alignItems:"center",marginBottom:height*0.03,cursor:"pointer" }}>
                    <span style={{ color:"#fff",fontSize:width*0.035,overflowWrap:"anywhere" }}>
                      {currentUser?.walletId || "Not set"}
                    </span>
                    <MdContentCopy size={width*0.05} color="#62fbfa" style={{ marginLeft:10 }}/>
                  </div>

                  {switchingName ? (
                    <>
                      <div className="spinner" style={{ margin:'24px 0' }}/>
                      <div style={styles.walletOptionButtonText2}>Switching username…</div>
                    </>
                  ) : (
                    <>
                      <button style={styles.walletOptionButton} onClick={toggleUseWalletId}>
                        Use Wallet ID as Username: {currentUser?.useWalletId ? "On" : "Off"}
                      </button>
                      <button style={{ ...styles.walletOptionButton, alignSelf:"flex-start",padding:"10px 20px" }} onClick={()=>setWalletIdView(false)}>
                        ‹ Back
                      </button>
                    </>
                  )}
                </>
              )
            )}
          </div>
        </div>
      )}

      {/* Confirm Purchase – DATCHAIN token path */}
      {confirmVisible && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalTitle}>Confirm Purchase</div>
            <div style={styles.modalText}>
              {needsDatTokens ? (
                <>
                  To purchase this DAT, you will need enough DATCHAIN Tokens. Visit{" "}
                  <a href="https://thedatchain.com/" target="_blank" rel="noreferrer" style={{ color:"#62fbfa", textDecoration:"underline" }}>
                    THEDATCHAIN.com
                  </a>{" "}
                  to learn more.
                </>
              ) : (
                <>
                  You are about to purchase {purchaseData?.SongName} DAT&nbsp;#{purchaseData?.selectedEdition} for $
                  {toNum(purchaseData?.editionDetails?.price).toFixed(2)}, paid entirely with your DATCHAIN Tokens.
                  {discountAndRoyaltyText.discount}
                  {discountAndRoyaltyText.royaltyDesc}
                </>
              )}
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.modalButton} onClick={handleCancel}>Cancel</button>
              {!needsDatTokens && <button style={styles.modalButton} onClick={handleConfirm}>Confirm</button>}
            </div>
          </div>
        </div>
      )}

      {/* legacy credit purchase UI left intact but unused by token flow */}
      <Modal
        isOpen={loadingPurchase}
        style={{
          overlay: { backgroundColor: "rgba(17, 14, 14, 0.75)", zIndex: 1000000 },
          content: {
            top: "50%", left: "50%", right: "auto", bottom: "auto",
            marginRight: "-50%", transform: "translate(-50%, -50%)",
            border: "none", background: "transparent", color: "white",
            borderRadius: "10px", padding: "20px", textAlign: "center", zIndex: 1000000
          }
        }}
      >
        <h2>Purchasing with credits...</h2>
      </Modal>

      {showStripeForm && stripeCS && (
        <div style={styles.modalOverlay}>
          <div style={styles.paymentContent}>
            <Elements stripe={stripePromise} options={{ clientSecret: stripeCS }}>
              <PaymentFlowDynamic
                amountLabel={`$${amountDueUSD} (includes Stripe fee)`}
                clientSecret={stripeCS}
                setIsPurchasing={setIsPurchasing}
                onPaymentSuccess={() => { setShowStripeForm(false); alert("Payment complete!"); }}
                onCancel={() => setShowStripeForm(false)}
              />
            </Elements>
          </div>
        </div>
      )}

      <div style={{ height: bottomAreaHeight }}/>
    </div>
  );
}

/* ───────────────── styles ───────────────── */
const styles = {
  fullScreen:{ position:"relative", width:"100%", height:"100vh", overflow:"hidden", fontFamily:"brhendrix" },
  fullScreenImage:{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", zIndex:0 },

  walletContainer:{
    position:"absolute", inset:0,
    top:height*0, bottom:bottomAreaHeight, paddingBottom: '350px',
    overflowY:"auto", padding:`0 ${width*0.01}px`, zIndex:1
  },
  scrollContainer:{ paddingBottom:'200px', marginTop: '20px' },

  /* NEW: totals cards (kept lightweight, inside scroll; won’t affect layout/scroll) */
  totalsGrid:{
    display:"grid",
    gridTemplateColumns:"repeat(2, minmax(0, 1fr))",
    gap: '12px',
    padding: '8px 4px',
    marginBottom: '8px'
  },
  metricCard:{
    background:"#181b2d",
    border:"1px solid rgba(98,251,250,0.25)",
    borderRadius:10,
    padding:"12px 10px",
    boxShadow:"0 4px 8px rgba(0,0,0,0.25)"
  },
  metricLabel:{ color:"rgba(255,255,255,0.72)", fontSize:12.5, marginBottom:6 },
  metricValue:{ color:"#62fbfa", fontSize:18, fontWeight:800 },

  /* asset row */
  datItemContainer:{
    display:"flex", alignItems:"center",
    background:"#211043", borderRadius:10,
    padding:width*0.01, margin:`${height*0.002}px 0`,
    cursor:"pointer"
  },
  thumbWrapper:{
    width:'110px', height:'110px',
    marginRight:'0px', borderRadius:5, justifyContent: 'center', alignSelf: 'center', overflow:"hidden",
    animation:"pulse 1s ease-out infinite"
  },
  datThumbnail:{ width:"80%",height:"80%",objectFit:"cover" },
  middleContainer:{ flex:1, display:"flex", flexDirection:"column" },
  datName:{ fontSize:'18px',color:"#fff",marginBottom:4 },
  datRoyalty:{ fontSize:'18px',color:"#fff" },
  datEarnings:{ fontSize:'18px',color:"#fff" },
  chartButton:{
    marginLeft:10,
    padding:`${height*0.0125}px ${width*0.021}px`,
    border:"1px solid #62fbfa", borderRadius:5,
    background:"transparent", color:"#fff", fontWeight:600, cursor:"pointer"
  },

  /* balance header (unchanged) */
  balanceContainer:{
    position:"absolute", top:'55px', left:0, width:"100%",
    height:height*0.075, background:"#0e061e",
    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end",
    borderBottom:"1px solid #ddd", zIndex:2
  },
  balanceLabel:{ fontSize:'18px',color:"#fff",marginBottom:height*0.005 },
  balanceOverlay:{ display:"flex",alignItems:"baseline",justifyContent:"center" },
  balanceSymbol:{ fontSize:'18px',color:"#fff",marginRight:5 },
  balanceText:{ fontSize:'18px',color:"#fff",fontWeight:700 },
  balanceCurrency:{ fontSize:'18px',color:"#fff",marginLeft:5 },

  /* modal (unchanged) */
  modalOverlay:{
    position:"fixed",inset:0,display:"flex",alignItems:"center",justifyContent:"center",
    background:"rgba(0,0,0,0.5)",zIndex:10
  },
  modalContent:{
    width:"90%",maxWidth:500,background:"#121523",borderRadius:10,padding:20,textAlign:"center"
  },
  modalTitle:{ fontSize:18,color:"#fff",marginBottom:10 },
  modalText:{ fontSize:16,color:"#ccc",marginBottom:20,whiteSpace:"pre-wrap" },
  modalButtons:{ display:"flex",gap:10,justifyContent:"center" },
  modalButton:{
    flex:1,padding:"10px 0",background:"#62fbfa",border:"none",borderRadius:5,
    fontWeight:700,cursor:"pointer", color:"#000"
  },

  paymentContent:{ width:"90%",maxWidth:540,background:"#121523",borderRadius:10,padding:20 },

buttonRow: {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',   // center the pair, like RN
  alignItems: 'center',
  width: '100%',
  gap: '24px',                 // even space between the two buttons
  padding: '0 4%',             // side breathing room
  margin: `${Math.round(height*0.01)}px 0 ${Math.round(height*0.014)}px`,
},
cashoutButton: {
  flex: 1,                     // both buttons same width
  maxWidth: 150,               // keeps them from getting too huge on desktop
  padding: '3px 0',           // RN-like vertical padding
  background: '#62fbfa',
  border: 'none',
  borderRadius: 6,             // RN rounded corners
  fontWeight: 800,             // bolder text
  fontSize: '18px',            // RN scale
  color: '#000',
  cursor: 'pointer',
},


  walletModalContent:{ width:"80%",maxWidth:420,background:"#181b2d",borderRadius:12, padding:"30px 40px",textAlign:"center" },
  walletModalTitle:{ fontSize:20,color:"#fff",marginBottom:24 },
  walletOptionButton:{ width:"100%",padding:"14px 0",marginTop:16,background:"#62fbfa", border:"none",borderRadius:6,fontWeight:700,cursor:"pointer" },
  walletOptionButtonText2:{ color:"#fff",fontSize:18,fontWeight:700 },

  /* misc images */
  fadeImage:{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", zIndex:3, pointerEvents:"none" }
};

/* optional CSS keyframes (save in VideoListScreen.css)
@keyframes pulse{
  0%{ transform:scale(1.1); }
  50%{ transform:scale(1.14); }
  100%{ transform:scale(1.1); }
}
*/
